import { useState } from 'react';
import { 
  Search, 
  Phone,
  Mail as MailIcon,
  Send,
  Users,
  Calendar,
  X,
  Check,
} from 'lucide-react';
import { useClients } from '../../../hooks/useClients';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { EnviarAvisoModal } from './EnviarAvisoModal';
import { toast } from 'sonner';

interface Cliente {
  id: number;
  nombre: string;
  email?: string;
  telefono?: string;
  direccion?: string;
  localidad?: string;
  provincia?: string;
  fechaRegistro: string;
  emailEnviado?: boolean;
}

export function AvisoList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientes, setSelectedClientes] = useState<Set<number>>(new Set());
  const [, setSelectAll] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [filterTipo, setFilterTipo] = useState<'todos' | 'conEmail' | 'sinEmail'>('todos');

  // Usar el hook correcto
  const { data: clientesData, isLoading, refetch } = useClients({ limit: 1000 });
  const clientes = clientesData?.data ?? [];

  // Filtrar clientes
  const filteredClientes = clientes.filter((cliente: any) => {
    // Filtro por búsqueda
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        cliente.nombre?.toLowerCase().includes(searchLower) ||
        cliente.email?.toLowerCase().includes(searchLower) ||
        cliente.telefono?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Filtro por tipo de email
    if (filterTipo === 'conEmail') {
      return cliente.email && cliente.email.trim() !== '';
    }
    if (filterTipo === 'sinEmail') {
      return !cliente.email || cliente.email.trim() === '';
    }

    return true;
  }) || [];

  // Clientes con email (para envíos masivos)
  const clientesConEmail = filteredClientes.filter(
    (c: any) => c.email && c.email.trim() !== ''
  );

  // Manejar selección individual
  const handleSelectCliente = (id: number) => {
    const newSelected = new Set(selectedClientes);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedClientes(newSelected);
    setSelectAll(newSelected.size === clientesConEmail.length && clientesConEmail.length > 0);
  };

  // Manejar envío individual
  const handleEnviarIndividual = (cliente: Cliente) => {
    if (!cliente.email) {
      toast.error('Este cliente no tiene email registrado');
      return;
    }
    setSelectedCliente(cliente);
    setShowEmailModal(true);
  };

  // Manejar envío masivo
  const handleEnviarMasivo = () => {
    if (selectedClientes.size === 0) {
      toast.error('Selecciona al menos un cliente');
      return;
    }
    const clientesSeleccionados = clientesConEmail.filter(
      (c: any) => selectedClientes.has(c.id)
    );
    if (clientesSeleccionados.length === 0) {
      toast.error('Los clientes seleccionados no tienen email');
      return;
    }
    setSelectedCliente(null);
    setShowBulkEmailModal(true);
  };

  // Limpiar selección
  const handleClearSelection = () => {
    setSelectedClientes(new Set());
    setSelectAll(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando clientes...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header con estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <Users size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{clientes.length}</p>
              <p className="text-sm text-gray-500">Total clientes</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-full">
              <MailIcon size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">
                {clientes.filter((c: any) => c.email && c.email.trim() !== '').length}
              </p>
              <p className="text-sm text-gray-500">Con email</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-full">
              <MailIcon size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">
                {selectedClientes.size}
              </p>
              <p className="text-sm text-gray-500">Seleccionados</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Controles */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar cliente por nombre, email o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex gap-2">
          <select
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
          >
            <option value="todos">Todos</option>
            <option value="conEmail">Con email</option>
            <option value="sinEmail">Sin email</option>
          </select>

          {selectedClientes.size > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleClearSelection}
              className="text-gray-600"
            >
              <X size={16} />
              Limpiar
            </Button>
          )}

          <Button
            size="sm"
            onClick={handleEnviarMasivo}
            disabled={selectedClientes.size === 0}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Send size={16} />
            <span className="hidden sm:inline ml-1">
              Enviar a {selectedClientes.size} seleccionados
            </span>
            <span className="sm:hidden">
              Enviar ({selectedClientes.size})
            </span>
          </Button>
        </div>
      </div>

      {/* Lista de clientes */}
      <div className="space-y-3">
        {filteredClientes.map((cliente: any) => {
          const tieneEmail = cliente.email && cliente.email.trim() !== '';
          const isSelected = selectedClientes.has(cliente.id);

          return (
            <Card key={cliente.id} className={`p-4 hover:shadow-md transition-shadow ${isSelected ? 'ring-2 ring-orange-400 bg-orange-50' : ''}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <button
                    onClick={() => handleSelectCliente(cliente.id)}
                    className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'border-gray-300 hover:border-orange-400'
                    }`}
                    disabled={!tieneEmail}
                  >
                    {isSelected && <Check size={14} />}
                  </button>

                  {/* Info del cliente */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-gray-800">{cliente.nombre}</h3>
                      {tieneEmail ? (
                        <Badge variant="info" className="bg-green-100 text-green-700">
                          <MailIcon size={12} className="mr-1" />
                          Con email
                        </Badge>
                      ) : (
                        <Badge variant="warning" className="bg-yellow-100 text-yellow-700">
                          <X size={12} className="mr-1" />
                          Sin email
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm">
                      <div className="flex items-center gap-2">
                        <MailIcon size={14} className="text-gray-400" />
                        <span className={tieneEmail ? 'text-blue-600' : 'text-gray-400'}>
                          {tieneEmail ? cliente.email : 'Sin email registrado'}
                        </span>
                      </div>
                      {cliente.telefono && (
                        <div className="flex items-center gap-2">
                          <Phone size={14} className="text-gray-400" />
                          <span className="text-gray-600">{cliente.telefono}</span>
                        </div>
                      )}
                      {cliente.localidad && (
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className="text-gray-400" />
                          <span className="text-gray-600">{cliente.localidad}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-xs">
                          ID: {cliente.id}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Acciones */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleEnviarIndividual(cliente)}
                    disabled={!tieneEmail}
                    className={tieneEmail ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-300 cursor-not-allowed'}
                  >
                    <Send size={16} />
                    <span className="hidden sm:inline ml-1">Enviar Aviso</span>
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
        {filteredClientes.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Users size={48} className="mx-auto mb-4 text-gray-300" />
            <p>No hay clientes para mostrar</p>
            <p className="text-sm mt-1">
              {searchTerm ? 'Intenta con otro término de búsqueda' : 'Los clientes aparecerán aquí cuando se registren'}
            </p>
          </div>
        )}
      </div>

      {/* Modal de envío individual */}
      {showEmailModal && selectedCliente && (
        <EnviarAvisoModal
          cliente={selectedCliente}
          onClose={() => {
            setShowEmailModal(false);
            setSelectedCliente(null);
          }}
          onSuccess={() => {
            refetch();
            setShowEmailModal(false);
            setSelectedCliente(null);
          }}
        />
      )}

      {/* Modal de envío masivo */}
      {showBulkEmailModal && (
        <EnviarAvisoModal
          cliente={null}
          clientesSeleccionados={clientesConEmail.filter((c: any) => selectedClientes.has(c.id)) as any}
          onClose={() => {
            setShowBulkEmailModal(false);
            setSelectedCliente(null);
          }}
          onSuccess={() => {
            refetch();
            setShowBulkEmailModal(false);
            setSelectedClientes(new Set());
            setSelectAll(false);
          }}
        />
      )}
    </div>
  );
}